import React, { 
  useEffect, useMemo, useState,
} from 'react';

import { FormControl, Radio } from '@primer/react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';

import BasicDropdown from '../common/BasicDropdown/BasicDropdown';
import Label from '../common/Labels/Label';
import TextInputWithError from '../common/TextInputs/TextInputWithError/TextInputWithError';
import useClientContext from '../contexts/ClientData/ClientData'

import ScrollableBox from '../common/overlays/ScrollableBox';
import SelectBaselineChart from './SelectBaselineChart';

import { TimeRangeProvider } from '../contexts/TimeRangeContext/TimeRangeContext';

import { AssayConfig, MonitorType } from './AssayConfig';

// TODO: Incremental typing.
interface CreateAssayStepOneProps {
  selectedModel: any
  setSelected: any
  applyMonitor: any
  setName: any
  setNextIsDisabled: any
  name: any
  models : any[]
  inferenceData: any,
  assayConfig: AssayConfig,
  setError: any,
  newAssayConfig: any,
  locations: undefined | string[],
  setNewAssayConfig: Function
}

interface SelectBaselineFrameWrapperProps {
  locations: undefined | string[]
  newAssayConfig: any
  hasRequiredFieldsForBaseline: boolean,
  setError: Function
  setNewAssayConfig: Function
  setBaselineDataFlag: Function
}

const INFERENCE_DATA_IN_KEYS_REGEX = /^(in)\.(.*)/;
const INFERENCE_DATA_OUT_KEYS_REGEX = /^((out))\.(.*)/;

const OUTPUT_PATH_JSON_INFERENCE = 'outputs';
const INPUT_PATH_JSON_INFERENCE = 'original_data.tensor';

// Takes an InferenceResult in either Arrow or WallarooJSON format and returns all data-related keys.
const getNamedFieldsFromInferenceResult = (inferenceResult: any, type: MonitorType = MonitorType.OUTPUT, isArrowEnabled: boolean = false) => {
  if (isArrowEnabled) {
    const regex = type === MonitorType.OUTPUT ? INFERENCE_DATA_OUT_KEYS_REGEX : INFERENCE_DATA_IN_KEYS_REGEX;
    return Object.keys(inferenceResult).filter(regex.test.bind(regex));
  }

  const path = type === MonitorType.OUTPUT ? OUTPUT_PATH_JSON_INFERENCE : INPUT_PATH_JSON_INFERENCE;

  return Object.keys(get(inferenceResult, path, {}));
}

const getColumnFieldsFromInferenceResult = (inferenceResult: any, namedField: string | null | undefined, type: MonitorType = MonitorType.OUTPUT, isArrowEnabled: boolean = false) => {
  if (!namedField) {
    return [];
  }

  // End users can have anything in their output data. For simplicity, Model Insights will only
  // support single numerics and flat arrays of numerics.
  let valuesAtPath;

  if (isArrowEnabled) {
    valuesAtPath = get(inferenceResult, namedField, []);
  } else if (type === MonitorType.OUTPUT) {
    valuesAtPath = get(inferenceResult, `${OUTPUT_PATH_JSON_INFERENCE}[${namedField}].Float.data`, []);
  } else {
    valuesAtPath = get(inferenceResult, `${INPUT_PATH_JSON_INFERENCE}[${namedField}]`, []);
  }

  if (isArray(valuesAtPath)) {
    return valuesAtPath.map((_s, i) => ({ label: i.toString(), id: i.toString() }));
  }
  
  if (Number.isFinite(valuesAtPath)) {
    return [{label: 'None', id: 'None'}];
  }

  // Intentional warning to help debugging.
  // eslint-disable-next-line no-console
  console.warn('Inference data shape cannot be monitored.', valuesAtPath);
  return [];
}

const SelectBaselineFrameWrapper: React.FC<SelectBaselineFrameWrapperProps> = ({
  newAssayConfig, setNewAssayConfig, setBaselineDataFlag, setError, hasRequiredFieldsForBaseline, locations,
}) => (
  // @ts-ignore
  <TimeRangeProvider usePollInterval={false} defaultTimeRange={{
    fromDate: newAssayConfig.baseline?.calculated?.fixed_window?.start ? new Date(newAssayConfig.baseline.calculated.fixed_window.start) : startOfDay(subDays(new Date(), 1)),
    toDate: newAssayConfig.baseline?.calculated?.fixed_window?.end ? new Date(newAssayConfig.baseline.calculated.fixed_window.end) : startOfDay(new Date()),
    type: 'custom'
  }}>
    <ScrollableBox hideShadows>
      <SelectBaselineChart
        setExternalError={setError}
        assayConfig={newAssayConfig}
        setAssayConfig={setNewAssayConfig}
        setBaselineDataFlag={setBaselineDataFlag}
        hasRequiredFieldsForBaseline={hasRequiredFieldsForBaseline}
        locations={locations}
      />
    </ScrollableBox>
  </TimeRangeProvider>
)

export const CreateAssayStepOne: React.FC<CreateAssayStepOneProps> = ({
  selectedModel,
  setSelected,
  // Apply monitor will also call setError(null)
  applyMonitor,
  setName,
  setNextIsDisabled,
  name,
  models = [],
  inferenceData,
  assayConfig,
  setError,
  newAssayConfig,
  setNewAssayConfig,
  locations,
}) => {
  const { monitored_type, monitored_column, monitored_field } = assayConfig;
  const { useArrowLogFormat } = useClientContext();
  const [hasBaselineData, setBaselineDataFlag] = useState(false);

  const outputFields = useMemo(() => getNamedFieldsFromInferenceResult(inferenceData, MonitorType.OUTPUT, useArrowLogFormat), [inferenceData, useArrowLogFormat]);
  const inputFields = useMemo(() => getNamedFieldsFromInferenceResult(inferenceData, MonitorType.INPUT, useArrowLogFormat), [inferenceData, useArrowLogFormat]);
  const namedFieldsListFormatted = (monitored_type === MonitorType.OUTPUT ? outputFields : inputFields).map(s => ({ label: s, id: s }));
  const columnFieldsListFormatted = getColumnFieldsFromInferenceResult(inferenceData, monitored_field, monitored_type, useArrowLogFormat);
  const hasRequiredFieldsForBaseline = !!monitored_column && !!monitored_field;

  if (monitored_field && columnFieldsListFormatted.length <= 0) {
    setError(`The shape of data ${monitored_field} is not currently supported.`);
  }

  const handleChangeRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const new_monitored_type = e.target.value === 'output' ? MonitorType.OUTPUT : MonitorType.INPUT;

    applyMonitor({ monitored_type: new_monitored_type, monitored_column: null, monitored_field: null });
  }

  const handleSelectNamedField = (e: string) => {
    applyMonitor({ monitored_field: e });
  }

  const handleSelectColumnField = (s: number | string) => {
    applyMonitor({ monitored_column: s })
  }

  // Handle the disabling of the Next button
  useEffect(() => {
    const allRequiredFieldsFilled: boolean = name?.length && monitored_field && monitored_column && hasBaselineData;
    setNextIsDisabled(!allRequiredFieldsFilled);
  }, [hasBaselineData, monitored_column, monitored_field, name, setNextIsDisabled])

  // Set the selected Model
  useEffect(() => {
    if (!selectedModel && models.length > 0) {
      setSelected(models[0].model_id);
    }
  }, [selectedModel, models, setSelected]);

  return (
    <div className="flex flex-col justify-start items-start">
      <Label className="mb-2" bold>Assay Name</Label>
      <TextInputWithError
        // @ts-ignore - Typescript infers the wrong type
        className="mb-4"
        placeholder="Please enter a name for your assay here"
        onEntryValidation={(entry: string) => entry?.length}
        onChange={setName}
        value={name}
        errorMessage="You must enter a name for your assay"
        block
      // It would be nice to make an extra API call to check the duplicate name constraint and show this error early.
      // errorMessage={displayError}
      />
      {// Hiding model select for now since we do not have the proper setup to monitor selected models
      /* <Label className="mb-2" bold>Select Model to monitor</Label>
      <BasicDropdown className="mb-3"
        values={models.map(m => ({ id: m.model_id, label: m.model_id }))}
        selectedValue={selectedModel}
        onValueSelected={(m: React.ChangeEvent<HTMLInputElement>) => { setSelected(m) }}
      /> */}
      <div className="flex space-x-4">
        <FormControl disabled={outputFields.length <= 0}>
          <FormControl.Label>Monitor output data</FormControl.Label>
          <Radio className="mb-2" value="output" name="dir" onChange={handleChangeRadio} checked={assayConfig.monitored_type === MonitorType.OUTPUT} />
        </FormControl>
        <FormControl disabled={inputFields.length <= 0}>
          <FormControl.Label>Monitor input data</FormControl.Label>
          <Radio className="mb-2" value="input" name="dir" onChange={handleChangeRadio} checked={assayConfig.monitored_type === MonitorType.INPUT} />
        </FormControl>
      </div>
      <span>Select an {assayConfig.monitored_type === MonitorType.OUTPUT ? 'output' : 'input'} to monitor</span>
      <div className="flex mt-2 space-x-4 relative">
        <BasicDropdown
          search
          values={namedFieldsListFormatted}
          onValueSelected={handleSelectNamedField}
          selectedValue={monitored_field}
          dropdownLabel="Named Field: "
          buttonLabelSize="sm"
          hideLabelInSmallViewports={false}
        />
        <BasicDropdown
          search
          values={columnFieldsListFormatted}
          isDisabled={!monitored_field || columnFieldsListFormatted.length <= 0}
          onValueSelected={handleSelectColumnField}
          selectedValue={monitored_column}
          dropdownLabel="Index: "
          buttonLabelSize="sm"
          hideLabelInSmallViewports={false}
        />
      </div>
      <div 
        className="relative my-4"
        style={{ width: '100%' }}
      >
        <SelectBaselineFrameWrapper
          locations={locations}
          newAssayConfig={newAssayConfig}
          setError={setError}
          setNewAssayConfig={setNewAssayConfig}
          setBaselineDataFlag={setBaselineDataFlag}
          hasRequiredFieldsForBaseline={hasRequiredFieldsForBaseline}
        />
      </div>
    </div>
  )
};

export default CreateAssayStepOne;
